/**
 * 进度条组件
 *
 * 显示任务进度百分比和阶段信息
 */

import React from 'react';
import { Progress, Tag, Space, Tooltip } from 'antd';
import {
    CloudDownloadOutlined,
    ThunderboltOutlined,
    SaveOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SyncOutlined,
} from '@ant-design/icons';

export interface ProgressBarProps {
    /** 进度百分比 (0-100) */
    progress: number;
    /** 当前阶段 */
    stage?: 'extracting' | 'embedding' | 'storing';
    /** 任务状态 */
    status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
    /** 已处理的切片数 */
    processedChunks?: number;
    /** 总切片数 */
    totalChunks?: number;
    /** 预计剩余时间 */
    estimatedRemaining?: string;
    /** 是否显示详情 */
    showDetail?: boolean;
    /** 大小 */
    size?: 'small' | 'default' | 'large';
}

/**
 * 阶段图标映射
 */
const StageIcons: Record<string, React.ReactNode> = {
    extracting: <CloudDownloadOutlined />,
    embedding: <ThunderboltOutlined />,
    storing: <SaveOutlined />,
};

/**
 * 阶段名称映射
 */
const StageNames: Record<string, string> = {
    extracting: '文本提取',
    embedding: '向量化',
    storing: '存储入库',
};

/**
 * 进度条组件
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
    progress,
    stage,
    status = 'processing',
    processedChunks,
    totalChunks,
    estimatedRemaining,
    showDetail = false,
    size = 'default',
}) => {
    // 确定进度条状态
    const getProgressStatus = () => {
        if (status === 'completed') return 'success';
        if (status === 'failed' || status === 'cancelled') return 'exception';
        if (status === 'pending') return 'normal';
        return 'active';
    };

    // 格式化进度信息
    const formatProgress = () => {
        if (status === 'pending') return '等待中';
        if (status === 'completed') return '已完成';
        if (status === 'failed') return '失败';
        if (status === 'cancelled') return '已取消';

        let text = `${progress}%`;
        if (processedChunks !== undefined && totalChunks !== undefined) {
            text += ` (${processedChunks}/${totalChunks})`;
        }
        return text;
    };

    // 获取状态图标
    const getStatusIcon = () => {
        switch (status) {
            case 'completed':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'failed':
            case 'cancelled':
                return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
            case 'pending':
                return <SyncOutlined spin />;
            default:
                return <SyncOutlined spin />;
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {/* 进度条 */}
                <Space style={{ width: '100%' }}>
                    {getStatusIcon()}
                    <Progress
                        percent={progress}
                        status={getProgressStatus() as any}
                        format={formatProgress}
                        size={size === 'small' ? 'small' : 'default'}
                        strokeColor={
                            status === 'completed'
                                ? '#52c41a'
                                : status === 'failed'
                                    ? '#ff4d4f'
                                    : '#1890ff'
                        }
                    />
                </Space>

                {/* 详情信息 */}
                {showDetail && (
                    <Space wrap size="small">
                        {/* 当前阶段 */}
                        {stage && (
                            <Tooltip title={`当前阶段：${StageNames[stage] || stage}`}>
                                <Tag icon={StageIcons[stage] || <SyncOutlined spin />} color="blue">
                                    {StageNames[stage] || stage}
                                </Tag>
                            </Tooltip>
                        )}

                        {/* 预计剩余时间 */}
                        {estimatedRemaining && (
                            <Tooltip title="预计剩余时间">
                                <Tag color="orange">⏱ {estimatedRemaining}</Tag>
                            </Tooltip>
                        )}

                        {/* 切片进度 */}
                        {processedChunks !== undefined && totalChunks !== undefined && (
                            <Tag color="purple">
                                📄 {processedChunks}/{totalChunks} 切片
                            </Tag>
                        )}
                    </Space>
                )}
            </Space>
        </div>
    );
};

/**
 * 紧凑版进度卡片（用于列表页）
 */
export const CompactProgressBar: React.FC<{
    progress: number;
    status?: string;
    stage?: string;
}> = ({ progress, status, stage }) => {
    return (
        <div style={{ width: 200 }}>
            <Progress
                percent={progress}
                size="small"
                status={
                    status === 'completed'
                        ? 'success'
                        : status === 'failed'
                            ? 'exception'
                            : 'active'
                }
                format={() => {
                    if (status === 'completed') return '✓';
                    if (status === 'failed') return '✗';
                    return `${progress}%`;
                }}
                strokeColor={
                    status === 'completed'
                        ? '#52c41a'
                        : status === 'failed'
                            ? '#ff4d4f'
                            : '#1890ff'
                }
            />
            {stage && (
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                    {StageNames[stage] || stage}
                </div>
            )}
        </div>
    );
};
